import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ChevronRight,
  CloudOff,
  LayoutGrid,
  LogOut,
  Settings,
  Share2,
  Users,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/hooks/use-current-user";
import { ThemeMenuGroup } from "@/components/common/theme-menu-group/theme-menu-group";
import { useSignOut } from "@/hooks/use-sign-out";
import { useOnlineStatus } from "@/offline/online-status";
import { useClaimOfflineChip } from "@/components/common/offline-chip/offline-chip-claim";
import { ProjectAccessChip } from "@/components/common/project-access-chip/project-access-chip";
import { RecordLimitIndicator } from "@/components/common/record-limit-indicator/record-limit-indicator";
import { ShareDialog } from "@/components/projects/share-dialog/share-dialog";
import { projectAccess } from "@/lib/project-access";
import { canViewRecords } from "@/lib/project-tabs";
import type { Action, MyRole } from "@/types/permissions";

interface ProjectHeaderProps {
  projectName: string | undefined;
  rightAction?: React.ReactNode;
  // Avatar menu data — when the project hasn't loaded yet, omit these
  // and the menu degrades gracefully to identity, Switch project, Appearance
  // and sign-out only.
  projectId?: string;
  recordsCount?: number;
  maxRecordCount?: number;
  memberCount?: number;
  // What the role chip is worked out from. The header already holds the
  // current user, so it works the level out itself.
  ownerId?: string;
  myRoles?: MyRole[];
  can?: (action: Action) => boolean;
}

// Touch-sized rows for the menu: about 44px, icons a step up from the default.
const MENU_ITEM_CLASS =
  "gap-3 px-3 py-2.5 text-[15px] [&_svg:not([class*='size-'])]:size-5";

function getInitials(firstName: string, lastName: string): string {
  const first = firstName.charAt(0);
  const last = lastName.charAt(0);
  return `${first}${last}`.toUpperCase() || "?";
}

export function ProjectHeader({
  projectName,
  rightAction,
  projectId,
  recordsCount,
  maxRecordCount,
  memberCount,
  ownerId,
  myRoles,
  can,
}: ProjectHeaderProps) {
  const signOut = useSignOut();
  const isOnline = useOnlineStatus();
  // Take over the global OfflineChip while this header is mounted — we
  // surface offline state inline beneath the project name instead.
  useClaimOfflineChip();
  const { data: profile } = useCurrentUser();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const displayName = profile
    ? [profile.first_name, profile.last_name].filter(Boolean).join(" ") ||
      profile.email
    : undefined;

  const initials = profile
    ? getInitials(profile.first_name, profile.last_name)
    : "?";

  const canAccessSettings =
    !!can && (can("projectConfig:Update") || can("member:View"));
  const canShare = !!can && can("member:Invite");

  // Owner / edit / view, the same chip the project cards show. Unknown until
  // the profile is, so it never shows a level that then changes.
  const access =
    ownerId !== undefined && myRoles
      ? projectAccess({ owner: ownerId, my_roles: myRoles }, profile?.id)
      : null;

  // Photo usage means nothing to someone who sees none of the photos — a
  // contributor. See [[project-header.spec]].
  const showRecordIndicator =
    typeof recordsCount === "number" &&
    typeof maxRecordCount === "number" &&
    !!can &&
    canViewRecords(can);

  async function handleSignOut() {
    setIsSigningOut(true);
    try {
      await signOut();
    } catch {
      // useSignOut() already wiped IDB and reported to Sentry. Reset the
      // trigger so the menu doesn't stay permanently disabled — the next
      // render will land on the offline sign-in gate.
      setIsSigningOut(false);
    }
  }

  function handleOpenSettings() {
    if (!projectId) return;
    navigate({
      to: "/projects/$projectId/settings",
      params: { projectId },
    });
  }

  function handleShare() {
    setShareDialogOpen(true);
  }

  function handleAllProjects() {
    navigate({ to: "/" });
  }

  return (
    <header
      className="sticky top-0 z-40 border-b border-border bg-background"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div className="grid min-h-14 grid-cols-[1fr_auto_1fr] items-center px-4 py-1">
        {/* Left: Avatar menu */}
        <div className="flex justify-start">
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={isSigningOut}>
              <button
                aria-label="Project menu"
                className="cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Avatar size="lg">
                  {profile?.avatar && (
                    <AvatarImage
                      src={profile.avatar}
                      alt={displayName ?? "User avatar"}
                    />
                  )}
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-[300px] max-w-[calc(100vw-1rem)]"
            >
              {/* Who you are: display only, first in the menu. */}
              {profile && (
                <>
                  <DropdownMenuLabel className="flex items-center gap-3 px-3 py-3 font-normal">
                    <Avatar className="size-11">
                      {profile.avatar && (
                        <AvatarImage src={profile.avatar} alt="" />
                      )}
                      <AvatarFallback className="text-base">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <p className="truncate text-base font-medium leading-tight">
                        {displayName}
                      </p>
                      <p className="truncate text-xs leading-tight text-muted-foreground">
                        {profile.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                </>
              )}

              {/* This project: the name, what you are to it, how full it is,
                  then what you can do in it. */}
              {projectName && (
                <DropdownMenuLabel className="flex items-center gap-2 px-3 pt-3 pb-1 text-xs font-semibold text-muted-foreground">
                  <span className="min-w-0 truncate">{projectName}</span>
                  {access && (
                    <ProjectAccessChip access={access} className="shrink-0" />
                  )}
                </DropdownMenuLabel>
              )}
              {showRecordIndicator && (
                <div className="px-2 py-1.5">
                  <RecordLimitIndicator
                    recordsCount={recordsCount}
                    maxRecordCount={maxRecordCount}
                  />
                </div>
              )}
              {projectId && canShare && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onSelect={handleShare}
                >
                  <Share2 aria-hidden="true" />
                  Share project
                  {typeof memberCount === "number" && (
                    <span className="ml-auto flex items-center gap-1 text-[13px] text-muted-foreground">
                      <Users aria-hidden="true" className="size-3.5" />
                      {memberCount}
                    </span>
                  )}
                </DropdownMenuItem>
              )}
              {canAccessSettings && (
                <DropdownMenuItem
                  className={MENU_ITEM_CLASS}
                  onSelect={handleOpenSettings}
                >
                  <Settings aria-hidden="true" />
                  Project settings
                  <ChevronRight
                    aria-hidden="true"
                    className="ml-auto size-4"
                  />
                </DropdownMenuItem>
              )}
              {/*
                The way back to the project list. It used to be a bottom-nav
                tab; Issues took that slot, and this menu is where the other
                leave-this-project actions already live. Unconditional — every
                user can always get out of a project.
              */}
              <DropdownMenuItem
                className={MENU_ITEM_CLASS}
                onSelect={handleAllProjects}
              >
                <LayoutGrid aria-hidden="true" />
                Switch project
                <ChevronRight aria-hidden="true" className="ml-auto size-4" />
              </DropdownMenuItem>

              {/* Switch project is always above, so this separator is
                  unconditional. */}
              <DropdownMenuSeparator />
              <ThemeMenuGroup />
              {isOnline && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    className={MENU_ITEM_CLASS}
                    disabled={isSigningOut}
                    onSelect={(event) => {
                      event.preventDefault();
                      void handleSignOut();
                    }}
                  >
                    <LogOut aria-hidden="true" />
                    {isSigningOut ? "Signing out…" : "Sign out"}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Center: Project name + inline offline indicator */}
        <div className="flex min-w-0 flex-col items-center justify-center gap-1 px-4">
          {projectName ? (
            <h1 className="max-w-48 truncate text-sm font-semibold sm:max-w-xs">
              {projectName}
            </h1>
          ) : (
            <Skeleton className="h-4 w-32" />
          )}
          {!isOnline && (
            <div
              role="status"
              aria-live="polite"
              data-testid="project-header-offline-indicator"
              className="flex items-center gap-1.5 rounded-full bg-foreground/95 px-2.5 py-0.5 text-[11px] font-medium text-background shadow-sm"
            >
              <CloudOff aria-hidden="true" className="size-3" />
              <span>Offline</span>
            </div>
          )}
        </div>

        {/* Right: Page-specific action slot */}
        <div className="flex justify-end">{rightAction}</div>
      </div>

      {projectId && canShare && (
        <ShareDialog
          projectId={projectId}
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
        />
      )}
    </header>
  );
}
